import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import React, { CSSProperties, useCallback, useMemo, Fragment, useEffect } from 'react';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';
import './form_upload.scss';
import { FormHelperText } from '@material-ui/core';
import { Label, UncontrolledTooltip } from 'reactstrap';
export interface FormUploadBaseProps<T extends File | any | File[] | any[] | any> {
  onChange?(value: T | null | any): void;
  style?: CSSProperties;
  accept?: string;
  image?: boolean;
  autoUpload?: boolean;
  autoUploadKey?: string;
  onRemove?(): void;
  description?: string;
  container?: string;
  isPrivate?: boolean
}

export interface FormUploadSingleProps<T extends File | any> extends FormUploadBaseProps<T>, FieldProps<FormControl<T>> {
  type?: 'single';
}
export interface FormUploadMultipleProps<T extends File[] | any[]> extends FormUploadBaseProps<T>, FieldProps<FormControl<T>> {
  type?: 'multiple';
}

export type FormUploadProps<T extends any> = FormUploadSingleProps<T> | FormUploadMultipleProps<any>;

// export type FormUploadProps = FieldProps<FormControl<string>>;

export function FormUpload<T extends any>(props: FormUploadProps<T>) {
  const { formControl, fieldState, fieldConfig } = useFormField(props);

  const fieldValue = fieldState.value || null;
  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;
  const isUploadResponse = useMemo(() => {
    return R.get(fieldValue, 'fileUrl') && R.get(fieldValue, 'fileName');
  }, [fieldValue]);

  const fieldReadonly = fieldConfig.readonly || null;
  const fieldDisabled = fieldConfig.disabled || null;

  const onUpload = useCallback(
    (file: File) => {
      if (props.autoUpload && props.autoUploadKey) {
        UploadAPI.upload(props.autoUploadKey, file, props.isPrivate)
          .pipe(tap(res => formControl.setValue(res as any)))
          .subscribe();
      } else {
        formControl.setValue(file as any);
      }
    },
    [formControl, props.autoUpload, props.autoUploadKey],
  );

  const onChange = useCallback(
    (files: FileList | null) => {
      if (!files) return null;
      if (props.type === 'multiple') {
        const data: File[] = [];
        for (let i = 0; i < files.length; i++) {
          data.push(files[i]);
        }

        formControl.setValue(data as any);
      } else {
        onUpload(files[0]);
        // formControl.setValue(files[0] as any);
      }
    },
    [formControl, onUpload, props.type],
  );


  const onClickView = (item: string) => {
    UploadAPI.getSignedUrl(item, true)
      .pipe(tap((data) => {
        const url = data.url;
        window.open(url, '_blank');
      }))
      .subscribe();
  }

  const label = useMemo(() => {
    let fieldLabel = fieldConfig.label;
    if (fieldValue) {
      if (props.type === 'multiple') {
        fieldLabel = (fieldValue as File[]).map(f => f.name).join(', ');
      } else {
        fieldLabel = (fieldValue as File).name;
      }
    }

    if (!R.isEmpty(fieldLabel)) {
      return fieldLabel;
    }
    return fieldConfig.label;
  }, [fieldConfig.label, fieldValue, props.type]);

  const tooltip = useMemo(() => {
    if (!props.description) return null;
    const uid = formControl.uid.replace('form:', '');
    return (
      <Fragment>
        <i className="fas fa-question-circle ml-2" id={`control-description-${uid}`}></i>
        <UncontrolledTooltip container={props.container} placement="top" target={`control-description-${uid}`}>
          {props.description}
        </UncontrolledTooltip>
      </Fragment>
    );
  }, [formControl.uid, props.container, props.description]);

  return (
    <React.Fragment>
      <div>
        {fieldConfig.label && (
          <Label for={formControl.uid}>
            {fieldConfig.label}
            {fieldConfig.required && !R.isEmpty(fieldConfig.label) && (
              <span className="text-danger font-size-14" style={{ marginTop: -5, marginLeft: 2 }}>
                *
              </span>
            )}
            {tooltip}
          </Label>
        )}
        <div className="custom-file mb-3">
          <input
            accept={props.accept || (props.image ? 'image/*' : undefined)}
            type="file"
            multiple={props.type === 'multiple'}
            className="custom-file-input"
            onChange={e => onChange(e.target.files)}
          />
          <label className="custom-file-label" htmlFor="customFile">
            {label}
          </label>
        </div>
        {props.isPrivate ? (isUploadResponse && <>
          <a href='javascript:void(0' onClick={() => onClickView(fieldValue.filePath)}>
            {fieldValue.fileName}
          </a>
        </>
        ) : (
          (isUploadResponse && <a href={fieldValue.fileUrl} target="_blank" rel="noopener noreferrer">
            {fieldValue.fileName}
          </a>)
        )}
        {R.isString(fieldValue) && props.image && !props.onRemove && <img src={fieldValue} style={{ width: 100, marginBottom: 20 }} />}
        {R.isString(fieldValue) && props.image && props.onRemove && (
          <div className="media-file" style={{ position: 'relative', width: '130px' }}>
            <i className="fa fa-trash-alt float-right upload-media-remove-icon" onClick={props.onRemove}></i>
            <img src={fieldValue} style={{ width: 100, marginBottom: 20 }} />
          </div>
        )}
        {!!fieldError && <FormHelperText style={{ color: '#f44336' }}>{fieldError}</FormHelperText>}
      </div>
    </React.Fragment>
  );
}
